import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, ChevronRight, AlertTriangle, Clock, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { differenceInDays, isPast } from "date-fns";
import type { Tables } from "@/integrations/supabase/types";

type ContentCalendar = Tables<"content_calendar">;

interface CalendarButtonProps {
  profileId: string;
}

export function CalendarButton({ profileId }: CalendarButtonProps) {
  const navigate = useNavigate();
  const [nextDeadline, setNextDeadline] = useState<ContentCalendar | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNextDeadline = async () => {
      try {
        const { data, error } = await supabase
          .from("content_calendar")
          .select("*")
          .eq("profile_id", profileId)
          .eq("is_completed", false)
          .order("deadline", { ascending: true })
          .limit(1)
          .maybeSingle();

        if (error) throw error;
        setNextDeadline(data);
      } catch (e) {
        console.error("[CalendarButton] Error fetching deadline:", e);
      } finally {
        setLoading(false);
      }
    };

    fetchNextDeadline();
  }, [profileId]);

  if (loading) {
    return (
      <div className="w-full h-20 rounded-xl bg-muted animate-pulse" />
    );
  }

  // Calculate days info
  const daysUntilDeadline = nextDeadline
    ? differenceInDays(new Date(nextDeadline.deadline), new Date())
    : null;

  const isOverdue = nextDeadline && isPast(new Date(nextDeadline.deadline));
  const daysOverdue = isOverdue ? Math.abs(daysUntilDeadline!) : 0;
  const fineApplies = daysOverdue >= 5;

  // Determine colors and status
  const getStatusConfig = () => {
    if (!nextDeadline) {
      return {
        bg: "bg-muted",
        text: "text-muted-foreground",
        icon: Calendar,
        label: "Nenhum prazo ativo",
        sublabel: "Aguardando calendário do admin",
      };
    }

    if (isOverdue && fineApplies) {
      return {
        bg: "bg-destructive/10 border-destructive",
        text: "text-destructive",
        icon: AlertTriangle,
        label: `${daysOverdue} DIAS DE ATRASO`,
        sublabel: "Multa de $500 aplicada",
      };
    }

    if (isOverdue) {
      return {
        bg: "bg-orange-500/10 border-orange-500",
        text: "text-orange-600",
        icon: AlertTriangle,
        label: `${daysOverdue} DIAS DE ATRASO`,
        sublabel: `${5 - daysOverdue} dias para multa`,
      };
    }

    if (daysUntilDeadline !== null && daysUntilDeadline <= 3) {
      return {
        bg: "bg-yellow-500/10 border-yellow-500",
        text: "text-yellow-600",
        icon: Clock,
        label: `${daysUntilDeadline} ${daysUntilDeadline === 1 ? "DIA RESTANTE" : "DIAS RESTANTES"}`,
        sublabel: "Prazo se aproximando!",
      };
    }

    return {
      bg: "bg-primary/5 border-primary",
      text: "text-primary",
      icon: Calendar,
      label: `${daysUntilDeadline} ${daysUntilDeadline === 1 ? "DIA RESTANTE" : "DIAS RESTANTES"}`,
      sublabel: "Próxima entrega de conteúdo",
    };
  };

  const config = getStatusConfig();
  const IconComponent = config.icon;

  return (
    <motion.button
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      onClick={() => navigate("/calendario")}
      className={cn(
        "w-full flex items-center justify-between p-4 rounded-xl border-2 shadow-lg hover:shadow-xl transition-all duration-300 group",
        config.bg
      )}
    >
      <div className="flex items-center gap-4">
        {/* Big Number or Icon */}
        <div className={cn(
          "w-16 h-16 rounded-xl flex items-center justify-center font-display text-3xl font-bold",
          config.text,
          nextDeadline ? "bg-white/50" : "bg-muted"
        )}>
          {nextDeadline ? (
            isOverdue ? `-${daysOverdue}` : daysUntilDeadline
          ) : (
            <IconComponent className="w-8 h-8" />
          )}
        </div>

        {/* Text Content */}
        <div className="flex flex-col items-start gap-0.5">
          <span className="font-display text-lg tracking-wide text-foreground">
            CALENDÁRIO
          </span>
          <span className={cn("text-sm font-medium", config.text)}>
            {config.label}
          </span>
          <span className="text-xs text-muted-foreground">
            {config.sublabel}
          </span>
        </div>
      </div>

      <ChevronRight className={cn(
        "w-6 h-6 transition-transform group-hover:translate-x-1",
        config.text
      )} />
    </motion.button>
  );
}
